<template>
  <widget-step-page
    :btn-next-disabled="false"
    :btn-prev-disabled="false"
    :btn-next-color="isPersonFormValid ? '#F39F18' : '#F7D091'"
    :on-btn-prev-clicked="onBtnPrevClicked"
    :on-btn-next-clicked="onBtnNextClicked"
  >
    <template #content>
      <div class="px-4 pb-14 pt-8">
        <div class="d-flex flex-column">
          <span
            class="mb-4"
            style="font-size: 20px; color: #2A3948"
          >Введите данные пациента:</span>

          <v-form
            ref="personForm"
            v-model="isPersonFormValid"
            autocomplete="off"
            onsubmit="return false"
            @keyup.enter="() => {}"
          >
            <v-layout wrap>
              <v-flex xs12>
                <x-text-field
                  v-model="localPatientItem.surname"
                  :rules="rules.surname"
                  placeholder="Введите фамилию"
                  class="pb-3"
                />
              </v-flex>
              <v-flex xs12>
                <x-text-field
                  v-model="localPatientItem.forename"
                  :rules="rules.forename"
                  placeholder="Введите имя"
                  class="pb-3"
                />
              </v-flex>
              <v-flex xs12>
                <x-text-field
                  v-model="localPatientItem.patronymic"
                  :rules="rules.patronymic"
                  placeholder="Введите отчество"
                  class="pb-3"
                />
              </v-flex>
              <v-flex
                xs12
                sm6
                class="pb-3"
                :class="{'pr-2': $vuetify.breakpoint.smAndUp}"
              >
                <x-date-picker
                  v-model="localPatientItem.birthdate"
                  placeholder="Введите дату рождения"
                  :date-picker-props="{ min: '1920-01-01', max: currentDate }"
                  :rules="rules.birthdate"
                />
              </v-flex>
              <v-flex
                xs12
                sm6
                class="pb-3"
              >
                <x-select
                  v-model="localPatientItem.gender"
                  :readonly="!isNullOrUndefined(localPatientItem.gender)"
                  :items="genderList"
                  :rules="rules.gender"
                  placeholder="Выберите пол"
                  :menu-props="{ disableKeys: false, offsetY: true, closeOnClick: true }"
                >
                  <template #item="{ item, on, attrs }">
                    <v-list-item
                      v-on="on"
                      v-bind="attrs"
                      :ripple="false"
                    >
                      <v-list-item-content>
                        <v-list-item-title style="white-space: normal; font-size: 16px;">
                          {{ item.text }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </x-select>
              </v-flex>
              <v-flex xs12>
                <x-text-field
                  v-model="localPatientItem.phone"
                  :rules="rules.phone"
                  placeholder="(___) ___ ____"
                  class="x-text-field__phone pb-3"
                  type="tel"
                  prefix="+7"
                  v-maska
                  data-maska="(###) ### ####"
                />
              </v-flex>
              <v-flex
                v-if="consentLink && policyLink"
                xs12
              >
                <v-checkbox
                  v-model="localPatientItem.agree"
                  dense
                  color="#F39F18"
                  :ripple="false"
                  on-icon="$checkboxOn"
                  off-icon="$checkboxOff"
                  :rules="rules.agree"
                >
                  <template #label>
                    <div style="color: #2A3948">
                      Я даю согласие ЧУЗ «ПОЛИКЛИНИКА ОВУМ» на обработку персональных данных, указанных в форме, в целях обработки обращения, обратной связи и/или предварительной записи на консультацию, на условиях
                      <a
                        :href="consentLink"
                        target="_blank"
                        style="color: #2A3948"
                        @click.stop
                      >
                        <u>Согласия посетителя сайта на обработку персональных данных</u>
                      </a>
                    </div>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-layout>
          </v-form>

          <span
            class="mb-4 pt-4"
            style="font-size: 20px; color: #2A3948"
          >Данные о записи:</span>
          <cumulative-info-panel/>
        </div>
      </div>
    </template>
  </widget-step-page>
</template>

<script>
import WidgetStepPage from "@/components/page-template/WidgetStepPage"
import {
  genderList,
  WIDGET_STEP__CONFIRM_PHONE,
  WIDGET_STEP__SELECT_SCHEDULE,
} from "@/plugins/consts"
import XTextField from "@/components/x-env/text-field/XTextField"
import XSelect from "@/components/x-env/select/XSelect"
import XDatePicker from "@/components/x-env/date-picker/XDatePicker"
import {isEmpty, isNullOrUndefined} from "@/plugins/object-helpers"
import CumulativeInfoPanel from "@/components/panels/CumulativeInfoPanel.vue"
import {DateTime} from "luxon"
import {mapGetters} from "vuex"

export default {
  components: {
    CumulativeInfoPanel,
    XDatePicker,
    XSelect,
    WidgetStepPage,
    XTextField,
  },
  data() {
    return {
      genderList,

      localPatientItem: {
        surname: null,
        forename: null,
        patronymic: null,
        gender: null,
        birthdate: null,
        phone: null,
        agree: false,
      },
      consentLink: null,
      policyLink: null,
      isPersonFormValid: false,
    }
  },
  computed: {
    ...mapGetters([
      'patientSurname',
      'patientForename',
      'patientPatronymic',
      'patientGender',
      'patientBirthdate',
      'patientPhone'
    ]),
    currentDate() {
      return DateTime.now().toFormat("yyyy-MM-dd")
    },
    rules() {
      return {
        surname: [
          v => !!v || '*Это поле обязательно для заполнения',
          v => (v && v.length <= 255) || 'Максимальная длина значения: 255 символов'
        ],
        forename: [
          v => !!v || '*Это поле обязательно для заполнения',
          v => (v && v.length <= 255) || 'Максимальная длина значения: 255 символов'
        ],
        patronymic: [
          v => !!v || '*Это поле обязательно для заполнения',
          v => (v && v.length <= 255) || 'Максимальная длина значения: 255 символов'
        ],
        gender: [
          v => !!v || '*Это поле обязательно для заполнения'
        ],
        birthdate: [
          v => !!v || '*Это поле обязательно для заполнения',
          v => this.isDateValid(v) || '*Укажите корректную дату рождения'
        ],
        phone: [
          v => !!v || '*Это поле обязательно для заполнения',
          v => (v && v.replace(/\D/g, '').length === 10) || '*Укажите корректный номер телефона'
        ],
        agree: [
          v => v === true || '*Подтверждение согласия обязательно (152-ФЗ)'
        ]
      }
    }
  },
  async mounted() {
    this.localPatientItem = {
      surname: this.patientSurname,
      forename: this.patientForename,
      patronymic: this.patientPatronymic,
      gender: this.patientGender,
      birthdate: this.patientBirthdate,
      phone: this.patientPhone,
      agree: false,
    }
    await this.getConsentAndPolicyLinks()
  },
  methods: {
    isNullOrUndefined,
    preparePatientInfo(val) {
      return {
        surname: val.surname,
        forename: val.forename,
        patronymic: val.patronymic,
        gender: val.gender,
        birthdate: val.birthdate,
        phone: isNullOrUndefined(val.phone) ? null : val.phone.replace(/\D/g, ''),
      }
    },

    onBtnPrevClicked() {
      this.$store.commit('clearPatientInfo')
      this.$emit('change-step', WIDGET_STEP__SELECT_SCHEDULE)
    },

    async onBtnNextClicked() {
      let valid = this.$refs.personForm.validate()
      if (!valid) {
        return
      }

      this.$store.commit('setPatientInfo', this.preparePatientInfo(this.localPatientItem))
      this.$emit('change-step', WIDGET_STEP__CONFIRM_PHONE)
    },
    /**
     * Получение согласий для выбранной услуги
     */
    async getConsentAndPolicyLinks() {
      this.$eventBus.$emit('show-overlay', true)
      await this.$axios
        .get(`${this.$backendLink}/widget/consent-and-policy`)
        .then(response => {
          this.consentLink = response.data.consent
          this.policyLink = response.data.policy
          if (!this.consentLink || !this.policyLink) {
            this.localPatientItem.agree = true
          }
        })
        .finally(() => {
          this.$eventBus.$emit('show-overlay', false)
        })
    },

    /**
     * Функция-правило для проверки корректности введённой даты
     *
     * @param v дата в формате dd.MM.yyyy (проверяется значение из v-text-field внутри x-date-picker)
     * @returns {boolean} true, если дата валидна, false - в противном случае
     */
    isDateValid(v) {
      if (isEmpty(v)) {
        return true
      }

      if (v.replace(/\D/g, '').length !== 8) {
        return false
      }

      let luxonDateTime = DateTime.fromFormat(v, 'dd.MM.yyyy')
      return luxonDateTime.isValid
        && luxonDateTime.startOf('day') >= DateTime.fromFormat('1920-01-01', 'yyyy-MM-dd')
        && luxonDateTime.startOf('day') <= DateTime.now().startOf('day');
    }
  }
}
</script>

<style scoped>

</style>